import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useUserRoles } from '@anytag/auth';
import { MIME_ACCEPT } from '@anytag/shared-constants';
import { ButtonSubmit, Form, Label } from '@anytag/shared-atoms';
import { useAllAvailableCountryOptions, useAllAdvertisersForSearchOptions } from '@anytag/shared-hooks';
import {
  TextField,
  SelectorField,
  PasswordField,
  CheckBoxField,
  UploadAgencyLogo,
  AsyncSearchHubspotCompanyField,
  SingleAsyncSearchHubspotCompanyFieldProps
} from '@anytag/shared-molecules';
import { AgencyFormValues, AgencyFormKeys } from './types';

export const Template = () => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { allAdvertiserOptions } = useAllAdvertisersForSearchOptions();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const {
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useFormContext<AgencyFormValues>();

  const values = watch();
  const isAddMode = values?.type === 'add';

  const handleClickSendEmail = (_: string, value: boolean) => {
    if (isAddMode) {
      // we need to clear password fields each time user click send email to prevent validation err on password
      reset({ ...values, password: '', passwordConfirm: '', hasInvitationEmail: value });
    }
  };

  const handleHubSpotChange: SingleAsyncSearchHubspotCompanyFieldProps<AgencyFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    const option = options?.find((option) => option.value === value);

    setValue(name, value, { shouldValidate: true });
    setValue('agencyName', option?.label || '');
    setValue('internalNetsuiteId', option?.internalNetsuiteId || '');
  };

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle label={t('Agency Information')} index={isAddMode ? 1 : undefined} />

        {isAdminStaff ? (
          <AsyncSearchHubspotCompanyField<AgencyFormKeys> name="hubspotId" onChange={handleHubSpotChange} />
        ) : null}

        <TextField<AgencyFormKeys>
          name="agencyName"
          help={t('Tooltip.Agency Name')}
          title={t('TextForm.Agency Name')}
          placeholder={t<string>('TextForm.AdAsiaLtd')}
          required
        />

        <SelectorField<AgencyFormKeys>
          title={t('Selector.Advertisers')}
          name="advertiserIds"
          help={t('Tooltip.User Advertiser')}
          options={allAdvertiserOptions}
          required
          multiple
        />

        <TextField<AgencyFormKeys>
          name="companyUrl"
          help={t('Tooltip.Agency URL')}
          title={t('TextForm.Agency URL')}
          placeholder="https://app.anytag.jp"
        />

        <Form.Fields itemsPerRow={2}>
          <TextField<AgencyFormKeys>
            name="phoneNumber"
            help={t('Tooltip.Phone Number')}
            title={t('TextForm.Phone Number')}
            placeholder="+66020485707"
          />

          <SelectorField<AgencyFormKeys>
            title={t('Selector.Country')}
            name="countryId"
            help={t('Tooltip.Agency Country')}
            options={allAvailableCountryOptions}
            required
          />
        </Form.Fields>

        <Form.Fields itemsPerRow={2}>
          <Form.Field css={styles.logoWrapper}>
            <Label title={t('Agency Logo')} help={t('Tooltip.customLogoHelp')} />
            <UploadAgencyLogo
              name="logo"
              multiple={false}
              hasError={!!errors.logo}
              value={values.logo ? [values.logo] : []}
              accept={{ ...MIME_ACCEPT.IMAGE, ...MIME_ACCEPT.GIF }}
              onChange={(_, value) => setValue('logo', value[0] || '')}
              notes={[
                t('General.DragAndDropInfo'),
                t('General.DragAndDropMaxSize', { maxSize: '10MB' }),
                t('General.DragAndDropMIME', { MIME: 'PNG, GIF, JPEG' }),
                t('General.DragAndDropImageRatio', { ratio: '16:9' })
              ]}
            />
          </Form.Field>
        </Form.Fields>

        {isAddMode ? (
          <>
            <Form.SectionTitle index={2} label={t('User Information')} className="mt-6" />

            <TextField<AgencyFormKeys>
              name="name"
              title={t('TextForm.Name')}
              placeholder={t<string>('NameExample')}
              required
            />

            <Form.Fields itemsPerRow={2}>
              <TextField<AgencyFormKeys>
                name="email"
                placeholder="your@email.com"
                title={t('TextForm.Email Address')}
                required
              />
            </Form.Fields>

            {!values.hasInvitationEmail && (
              <Form.Fields itemsPerRow={2}>
                <PasswordField<AgencyFormKeys> name="password" title={t('Password.Password')} dynamicError />
                <PasswordField<AgencyFormKeys>
                  name="passwordConfirm"
                  title={t('Password.Confirm Password')}
                  dynamicError
                />
              </Form.Fields>
            )}

            <CheckBoxField<AgencyFormKeys>
              name="hasInvitationEmail"
              onChange={handleClickSendEmail}
              label={t('Send invitation email')}
            />
          </>
        ) : null}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit title={t('Button.Save')} loading={isSubmitting} />
      </Form.Footer>
    </>
  );
};

const styles = {
  logoWrapper: css({
    '& > div > div:first-of-type': {
      border: '1px dashed #e0e8ed',
      '> div > i': { margin: 0, border: 'none', padding: '8px 24px 24px' }
    }
  })
};
